import { Box } from '@televet/kibble-ui';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { useCallback, useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  PermissionType,
  SortOrder,
  useGetWidgetRequestTypesTableQuery,
  useUpdateOneClinicWidgetRequestTypeMutation,
  WidgetRequestType
} from 'shared/types/graphql';
import { WidgetRequestTypeRow } from '../../types';
import WidgetRequestsTable from './components/WidgetRequestsTable';

const WidgetRequestsSection = (): JSX.Element => {
  const toast = useToast();
  const { currentClinicId, userHasPermission } = useClinicUser();
  const hasPermission = userHasPermission(PermissionType.WriteClinic);
  const { data: settingsData, loading } = useGetWidgetRequestTypesTableQuery({
    variables: {
      where: {
        isActive: {
          equals: true,
        },
        isDeleted: {
          not: {
            equals: true,
          },
        },
        requestType: {
          notIn: [
            WidgetRequestType.TalkToStaff,
            WidgetRequestType.InboundSms,
            WidgetRequestType.DirectBooking,
            WidgetRequestType.Teletriage,
          ],
        },
        clinicWidgetSetting: {
          clinic: {
            some: {
              id: {
                equals: currentClinicId,
              },
            },
          },
        },
      },
      orderBy: [{ order: SortOrder.Asc }],
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const data = useMemo(
    () =>
      settingsData?.findManyClinicWidgetRequestType?.map((requestType) => ({
        ...requestType,
        selectedAssigneesId: requestType.assignees.map((assignee) => assignee.id),
      })) || [],
    [settingsData?.findManyClinicWidgetRequestType],
  );

  const [updateOneClinicWidgetRequestType] = useUpdateOneClinicWidgetRequestTypeMutation();

  const updateTaskCreation = useCallback(
    (id: string, shouldCreate: boolean) => {
      updateOneClinicWidgetRequestType({
        variables: {
          where: { id },
          data: { shouldCreateTask: shouldCreate },
        },
      });
    },
    [updateOneClinicWidgetRequestType],
  );

  const updateAssignees = useCallback(
    (widgetRequestType: WidgetRequestTypeRow, assigneeId: string, isAdding: boolean) => {
      if (!isAdding && widgetRequestType.selectedAssigneesId.length === 1) {
        return toast({
          title: "Cannot remove last assignee",
          description: "At least one assignee must be selected.",
          status: "error",
        });
      };

      updateOneClinicWidgetRequestType({
        variables: {
          where: { id: widgetRequestType.id },
          data: {
            assignees: isAdding
              ? {
                connect: [
                  {
                    id: assigneeId,
                  },
                ],
              }
              : { disconnect: [{ id: assigneeId }] },
          },
        },
      });
    },
    [updateOneClinicWidgetRequestType, toast],
  );

  return (
    <Box>
      <Box mb={5}>
        <Heading size="sm" mb={2}>Widget Requests</Heading>
        <Text as="p" size="sm" color="text.subtle">Configure which active widget requests create a task and any automatic assignees.</Text>
      </Box>
      {loading ? (
        <Spinner size="sm" />
      ) : (
          <>
            {!data?.length ? (
              <Alert
                status='info'
                width="auto"
                hideCloseButton
                justifySelf="flex-start"
                title={
                  <Box>
                    <Text as="p" size="sm" fontWeight="bold">You don’t have any widget requests set up.</Text>
                    <Text as="p" size="sm" fontWeight="normal">
                      Want tasks created and assigned automatically from widget requests? Go to{" "}<Link href="/settings/clinic/widget" size="sm">Website Widget settings</Link>
                    </Text>
                  </Box>
                }
              />
            ) : (
                <WidgetRequestsTable
                  data={data}
                  updateTaskCreation={updateTaskCreation}
                  updateAssignees={updateAssignees}
                  hasPermission={hasPermission}
                />
              )}
          </>
        )}
    </Box>
  );
};

export default WidgetRequestsSection;
